import type { ApiResponse, EncryptedBlob } from '@shared/types';
import { signedIdentityRequest } from './signedIdentityApi';

const MESSAGE_ARCHIVE_SCOPE = 'message archive';

export type MessageArchiveDestinationType = 'circle';
export type MessageArchiveMode = 'text';
export type MessageArchiveStatus = 'active' | 'paused' | 'disabled';

export type MessageArchiveJob = {
  archiveId: string;
  ownerIdentityId: string;
  writerDeviceId: string;
  destinationType: MessageArchiveDestinationType;
  destinationConfig: { circle: 'current' } | Record<string, unknown>;
  mode: MessageArchiveMode;
  wrappedArchiveKey: EncryptedBlob | Record<string, unknown> | null;
  archiveKeyVersion: number;
  encryptedManifest: EncryptedBlob | Record<string, unknown> | null;
  manifestRevision: number;
  status: MessageArchiveStatus;
  lastArchivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type MessageArchiveSegment = {
  segmentId: string;
  archiveId: string;
  periodKey: string;
  state: 'current' | 'final';
  revision: number;
  periodStartedAt: string | null;
  periodEndedAt: string | null;
  messageCount: number;
  byteSize: number;
  encryptedSegment: EncryptedBlob | Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export const messageArchiveApi = {
  async getJob(
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ job: MessageArchiveJob | null }>> {
    return signedIdentityRequest({
      path: '/message-archive/job/get',
      type: 'archive:job:get',
      payload: {},
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async enableJob(
    payload: {
      destinationType?: MessageArchiveDestinationType;
      mode?: MessageArchiveMode;
      wrappedArchiveKey: EncryptedBlob | Record<string, unknown>;
      encryptedManifest?: EncryptedBlob | Record<string, unknown> | null;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ job: MessageArchiveJob }>> {
    return signedIdentityRequest({
      path: '/message-archive/job/enable',
      type: 'archive:job:enable',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async updateManifest(
    payload: {
      archiveId: string;
      expectedRevision: number;
      encryptedManifest: EncryptedBlob | Record<string, unknown>;
      lastArchivedAt?: string | null;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ job: MessageArchiveJob }>> {
    return signedIdentityRequest({
      path: '/message-archive/job/manifest',
      type: 'archive:job:manifest',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async disableJob(
    archiveId: string,
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ job: MessageArchiveJob | null }>> {
    return signedIdentityRequest({
      path: '/message-archive/job/disable',
      type: 'archive:job:disable',
      payload: { archiveId },
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async updateWriter(
    archiveId: string,
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ job: MessageArchiveJob }>> {
    return signedIdentityRequest({
      path: '/message-archive/job/writer',
      type: 'archive:job:writer',
      payload: { archiveId },
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async putSegment(
    payload: {
      archiveId: string;
      periodKey: string;
      expectedRevision?: number | null;
      periodStartedAt?: string | null;
      periodEndedAt?: string | null;
      messageCount?: number;
      byteSize?: number;
      encryptedSegment: EncryptedBlob | Record<string, unknown>;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ segment: { segmentId: string; archiveId: string; periodKey: string; state: 'current' | 'final'; revision: number; createdAt: string; updatedAt: string } }>> {
    return signedIdentityRequest({
      path: '/message-archive/segments/put',
      type: 'archive:segments:put',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async finalizeSegment(
    payload: {
      archiveId: string;
      periodKey: string;
      expectedRevision: number;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ segment: { segmentId: string; archiveId: string; periodKey: string; state: 'current' | 'final'; revision: number; updatedAt: string } }>> {
    return signedIdentityRequest({
      path: '/message-archive/segments/finalize',
      type: 'archive:segments:finalize',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  },

  async listSegments(
    payload: {
      archiveId: string;
      afterPeriodKey?: string | null;
      limit?: number;
    },
    serverUrl: string | undefined,
    localIdentityId: string
  ): Promise<ApiResponse<{ segments: MessageArchiveSegment[] }>> {
    return signedIdentityRequest({
      path: '/message-archive/segments/list',
      type: 'archive:segments:list',
      payload,
      serverUrl,
      localIdentityId,
      scopeLabel: MESSAGE_ARCHIVE_SCOPE
    });
  }
};
